<template>
  <Loader v-if="loader" />
  <div class="flex" style="display: flex; justify-content: flex-start; margin: 20px">
    <InputText
      v-model="searchTerm"
      placeholder="Cerca..."
      class="p-inputtext p-component"
      style="width: 300px; margin-right: 10px;"
    />
    <Button
      :label="loader ? 'Caricamento...' : 'Cerca'"
      :icon="loader ? 'pi pi-spinner pi-spin' : 'pi pi-search'"
      class="p-button-primary"
      @click="onSearch"
      :disabled="loader"
    />
  </div>
  <hr style="margin: 20px 0; border-top: 0.5px solid #ccc;" />

  <div class="table-container" style="overflow-x: auto">
    <DataTable
      :value="filteredRows"
      paginator
      :rows="20"
      dataKey="id"
      emptyMessage="Nessuna pratica firmata da confermare"
      responsiveLayout="scroll"
    >
      <Column field="tipo_label" header="Tipologia"></Column>
      <Column field="code" header="POD / PDR">
        <template #body="{ data }">
          {{ data.code || data.mapped?.code || '-' }}
        </template>
      </Column>
      <Column field="nominativo" header="Nominativo"></Column>
      <Column field="cliente_nome" header="Cliente"></Column>
      <Column field="email" header="Email"></Column>
      <Column field="signed_at" header="Firmata il">
        <template #body="{ data }">
          {{ formatDate(data.signed_at) }}
        </template>
      </Column>
      <Column field="created_by" header="Inviata da"></Column>
      <Column header="Azioni" style="min-width: 310px">
        <template #body="{ data }">
          <Button
            icon="pi pi-download"
            class="p-button-sm p-button-outlined mr-2"
            label="Firmato"
            :disabled="downloadingId === data.id"
            @click="downloadSigned(data)"
          />
          <Button
            icon="pi pi-check"
            class="p-button-sm p-button-success mr-2"
            label="Conferma"
            @click="openConfirm(data)"
          />
          <Button
            icon="pi pi-times"
            class="p-button-sm p-button-danger p-button-outlined"
            label="Scarta"
            @click="openDiscard(data)"
          />
        </template>
      </Column>
    </DataTable>
  </div>

  <div class="discarded-section">
    <button type="button" class="discarded-toggle" @click="showDiscarded = !showDiscarded">
      <i :class="showDiscarded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
      Scartate ({{ filteredDiscardedRows.length }})
    </button>
    <div v-if="showDiscarded" class="table-container" style="overflow-x: auto">
      <DataTable
        :value="filteredDiscardedRows"
        paginator
        :rows="10"
        dataKey="id"
        emptyMessage="Nessuna pratica scartata"
        responsiveLayout="scroll"
      >
        <Column field="tipo_label" header="Tipologia"></Column>
        <Column field="code" header="POD / PDR">
          <template #body="{ data }">
            {{ data.code || data.mapped?.code || '-' }}
          </template>
        </Column>
        <Column field="nominativo" header="Nominativo"></Column>
        <Column field="cliente_nome" header="Cliente"></Column>
        <Column field="email" header="Email"></Column>
        <Column field="signed_at" header="Firmata il">
          <template #body="{ data }">
            {{ formatDate(data.signed_at) }}
          </template>
        </Column>
        <Column field="created_by" header="Inviata da"></Column>
        <Column field="nota_scarto" header="Nota" style="min-width: 220px">
          <template #body="{ data }">
            {{ data.nota_scarto || '-' }}
          </template>
        </Column>
        <Column header="Azioni" style="min-width: 280px">
          <template #body="{ data }">
            <Button
              icon="pi pi-download"
              class="p-button-sm p-button-outlined mr-2"
              label="Firmato"
              :disabled="downloadingId === data.id"
              @click="downloadSigned(data)"
            />
            <Button
              icon="pi pi-check"
              class="p-button-sm p-button-success"
              label="Lavorata"
              :disabled="workingId === data.id"
              @click="openWorked(data)"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>

  <Dialog
    v-model:visible="dialogVisible"
    modal
    header="Conferma pratica Incall"
    :style="{ width: '52rem' }"
  >
    <div v-if="selected" class="riepilogo">
      <p class="riepilogo-intro">
        <b>{{ selected.tipo_label }}</b>
        — riepilogo dati che saranno inseriti in
        {{ destinationLabel(selected.tipo) }}. I campi non sono modificabili.
      </p>

      <div class="riepilogo-grid">
        <div
          v-for="item in summaryFields"
          :key="item.label"
          class="riepilogo-row"
        >
          <span class="riepilogo-label">{{ item.label }}</span>
          <span class="riepilogo-value">{{ displayValue(item.value) }}</span>
        </div>
      </div>
    </div>
    <template #footer>
      <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="dialogVisible = false" />
      <Button
        label="Conferma e inserisci"
        icon="pi pi-check"
        class="p-button-success"
        :loading="confirming"
        @click="confirmPratica"
      />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="discardDialogVisible"
    modal
    header="Scarta pratica"
    :style="{ width: '32rem' }"
  >
    <p v-if="selected">
      Vuoi scartare la pratica <b>{{ selected.tipo_label }}</b>
      <span v-if="selected.code || selected.mapped?.code">
        ({{ selected.code || selected.mapped?.code }})
      </span>?
    </p>
    <div class="p-field mt-3">
      <label for="nota-scarto"><b>Nota</b></label>
      <Textarea
        id="nota-scarto"
        v-model="discardNote"
        rows="4"
        autoResize
        placeholder="Motivo dello scarto"
        class="w-full"
      />
    </div>
    <template #footer>
      <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="discardDialogVisible = false" />
      <Button
        label="Scarta"
        icon="pi pi-times"
        class="p-button-danger"
        :loading="discarding"
        :disabled="!discardNote.trim()"
        @click="discardPratica"
      />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="workedDialogVisible"
    modal
    header="Pratica lavorata"
    :style="{ width: '32rem' }"
  >
    <p v-if="selected">
      La pratica corretta è arrivata. Segnare
      <b>{{ selected.tipo_label }}</b>
      <span v-if="selected.code || selected.mapped?.code">
        ({{ selected.code || selected.mapped?.code }})
      </span>
      come lavorata? Sparirà dalle scartate.
    </p>
    <template #footer>
      <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="workedDialogVisible = false" />
      <Button
        label="Lavorata"
        icon="pi pi-check"
        class="p-button-success"
        :loading="working"
        @click="markWorked"
      />
    </template>
  </Dialog>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import Service from "@/services/ask-pratiche/gestione-pratiche";
import Loader from "@/components/Loader.vue";
import { useToast } from "primevue/usetoast";

export default {
  components: { Loader },
  props: {
    data: Array,
  },
  setup(props) {
    const toast = useToast();
    const service = new Service();
    const loader = ref(false);
    const confirming = ref(false);
    const discarding = ref(false);
    const working = ref(false);
    const workingId = ref(null);
    const downloadingId = ref(null);
    const searchTerm = ref("");
    const rows = ref([]);
    const discardedRows = ref([]);
    const showDiscarded = ref(false);
    const dialogVisible = ref(false);
    const discardDialogVisible = ref(false);
    const workedDialogVisible = ref(false);
    const selected = ref(null);
    const form = ref({});
    const discardNote = ref("");

    const loadRows = async () => {
      loader.value = true;
      try {
        const res = await service.getListPraticheStalloIncall();
        if (Array.isArray(res)) {
          rows.value = res;
          discardedRows.value = [];
        } else {
          rows.value = res?.pending || res?.data || [];
          discardedRows.value = res?.rejected || [];
        }
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Impossibile caricare le pratiche da confermare",
          life: 3000,
        });
      } finally {
        loader.value = false;
      }
    };

    watch(
      () => props.data,
      (value) => {
        if (Array.isArray(value) && value.length) {
          rows.value = value;
        }
      },
      { immediate: true }
    );

    const matchesSearch = (row) => {
      const term = searchTerm.value.trim().toLowerCase();
      if (!term) return true;
      const hay = [
        row.tipo_label,
        row.code,
        row.nominativo,
        row.cliente_nome,
        row.email,
        row.mapped?.code,
        row.nota_scarto,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    };

    const filteredRows = computed(() => rows.value.filter(matchesSearch));
    const filteredDiscardedRows = computed(() => discardedRows.value.filter(matchesSearch));

    const formatDate = (value) => {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString("it-IT");
    };

    const destinationLabel = (tipo) => {
      return {
        cessazione: "Cessazione",
        mc1: "MC1",
        verifica: "Verifica contatore",
        spostamento: "Spostamento contatore",
      }[tipo] || tipo;
    };

    const displayValue = (value) => {
      if (value === null || value === undefined || value === "") return "-";
      return value;
    };

    const summaryFields = computed(() => {
      const f = form.value || {};
      const common = [
        { label: "POD / PDR", value: f.code },
        { label: "Email", value: f.email },
        { label: "Telefono", value: f.telefono },
      ];

      const byTipo = {
        cessazione: [
          { label: "Nome", value: f.nomerappresentante },
          { label: "Cognome", value: f.cognomerappresentante },
          { label: "Data esecuzione", value: f.data_esecuzione },
          { label: "Rimozione", value: f.info_contatore },
          { label: "Codice Ateco", value: f.codiceAteco },
        ],
        mc1: [
          { label: "Nome", value: f.nomerappresentante },
          { label: "Cognome", value: f.cognomerappresentante },
          { label: "Codice fiscale", value: f.codicefiscale },
          { label: "Potenza disponibile", value: f.potenzaDisponibile },
          { label: "Potenza impegnata", value: f.potenzaImpegnata },
          { label: "Stagionalità", value: f.stagionalità },
          { label: "Tensione", value: f.tensione },
          { label: "Tipologia", value: f.tipologiaContrattuale },
        ],
        verifica: [
          { label: "Nome", value: f.nome },
          { label: "Cognome", value: f.cognome },
          { label: "Intestatario", value: f.intestatario },
          { label: "Ragione sociale", value: f.ragsoc },
          { label: "P.IVA", value: f.piva },
          { label: "Codice fiscale", value: f.codiceFiscale },
          { label: "Motivazione", value: f.motivazione },
        ],
        spostamento: [
          { label: "Nome", value: f.nomerappresentante },
          { label: "Cognome", value: f.cognomerappresentante },
          { label: "Ragione sociale", value: f.ragionesociale },
          { label: "P.IVA / CF", value: f.piva },
          { label: "Indirizzo", value: f.indirizzo },
          { label: "CAP", value: f.cap },
          { label: "PR", value: f.pr },
          { label: "Tipologia", value: f.tipo },
        ],
      };

      const tipo = selected.value?.tipo;
      return [...common, ...(byTipo[tipo] || byTipo.spostamento)];
    });

    const onSearch = () => {};

    const openConfirm = (row) => {
      selected.value = row;
      form.value = { ...(row.mapped || {}) };
      dialogVisible.value = true;
    };

    const openDiscard = (row) => {
      selected.value = row;
      discardNote.value = "";
      discardDialogVisible.value = true;
    };

    const openWorked = (row) => {
      selected.value = row;
      workedDialogVisible.value = true;
    };

    const downloadSigned = async (row) => {
      downloadingId.value = row.id;
      try {
        await service.downloadPraticaStalloIncall(row.id);
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Download del PDF firmato non riuscito",
          life: 3000,
        });
      } finally {
        downloadingId.value = null;
      }
    };

    const confirmPratica = async () => {
      if (!selected.value) return;
      confirming.value = true;
      try {
        const res = await service.confermaPraticaStalloIncall(selected.value.id, form.value);
        if (!res?.success) {
          toast.add({
            severity: "error",
            summary: "Errore",
            detail: res?.message || "Conferma non riuscita",
            life: 4000,
          });
          return;
        }
        toast.add({
          severity: "success",
          summary: "Confermata",
          detail: res.message,
          life: 3000,
        });
        dialogVisible.value = false;
        await loadRows();
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: error?.response?.data?.message || "Conferma non riuscita",
          life: 4000,
        });
      } finally {
        confirming.value = false;
      }
    };

    const discardPratica = async () => {
      if (!selected.value) return;
      const nota = discardNote.value.trim();
      if (!nota) {
        toast.add({
          severity: "warn",
          summary: "Nota obbligatoria",
          detail: "Inserisci una nota per scartare la pratica",
          life: 3000,
        });
        return;
      }
      discarding.value = true;
      try {
        const res = await service.scartaPraticaStalloIncall(selected.value.id, nota);
        if (!res?.success) {
          toast.add({
            severity: "error",
            summary: "Errore",
            detail: res?.message || "Scarto non riuscito",
            life: 4000,
          });
          return;
        }
        toast.add({
          severity: "success",
          summary: "Scartata",
          detail: res.message || "Pratica scartata",
          life: 3000,
        });
        discardDialogVisible.value = false;
        showDiscarded.value = true;
        await loadRows();
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: error?.response?.data?.message || "Scarto non riuscito",
          life: 4000,
        });
      } finally {
        discarding.value = false;
      }
    };

    const markWorked = async () => {
      if (!selected.value) return;
      working.value = true;
      workingId.value = selected.value.id;
      try {
        const res = await service.marcaLavorataPraticaStalloIncall(selected.value.id);
        if (!res?.success) {
          toast.add({
            severity: "error",
            summary: "Errore",
            detail: res?.message || "Impossibile segnare la pratica come lavorata",
            life: 4000,
          });
          return;
        }
        toast.add({
          severity: "success",
          summary: "Lavorata",
          detail: res.message || "Pratica tolta dalle scartate",
          life: 3000,
        });
        workedDialogVisible.value = false;
        await loadRows();
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: error?.response?.data?.message || "Impossibile segnare la pratica come lavorata",
          life: 4000,
        });
      } finally {
        working.value = false;
        workingId.value = null;
      }
    };

    onMounted(() => {
      loadRows();
    });

    return {
      loader,
      confirming,
      discarding,
      working,
      workingId,
      downloadingId,
      searchTerm,
      filteredRows,
      filteredDiscardedRows,
      showDiscarded,
      dialogVisible,
      discardDialogVisible,
      workedDialogVisible,
      selected,
      form,
      discardNote,
      onSearch,
      formatDate,
      destinationLabel,
      displayValue,
      summaryFields,
      openConfirm,
      openDiscard,
      openWorked,
      downloadSigned,
      confirmPratica,
      discardPratica,
      markWorked,
    };
  },
};
</script>

<style scoped>
.discarded-section {
  margin: 24px 20px 20px;
}

.discarded-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 600;
  color: #334155;
}

.discarded-toggle:hover {
  background: #f1f5f9;
}

.discarded-toggle i {
  font-size: 0.8rem;
}

.riepilogo-intro {
  margin: 0 0 1rem;
  color: #475569;
}

.riepilogo-grid {
  display: grid;
  gap: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.riepilogo-row {
  display: grid;
  grid-template-columns: minmax(140px, 34%) 1fr;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.riepilogo-row:nth-child(even) {
  background: #f8fafc;
}

.riepilogo-row:last-child {
  border-bottom: none;
}

.riepilogo-label {
  font-weight: 600;
  color: #334155;
}

.riepilogo-value {
  color: #0f172a;
  word-break: break-word;
}
</style>
